// frontend/src/pages/IPTU.js
import React, { useState, useEffect } from "react";
import "./CadastroImoveis.css";
import { FaCopy, FaEdit, FaEye, FaTrash, FaSave, FaTimes, FaPaperclip } from "react-icons/fa";
import { jwtDecode } from "jwt-decode";
import { copiarTexto } from "../utils/clipboard";
import BuscarMatricula from "../components/BuscarMatricula";
import useRequiredFields from "../hooks/useRequiredFields";
import ThemeProvider from "../theme/TemaMuiProvider";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import muiTheme from "../theme/muiTheme";

/**
 * ✅ Máscara BR (AJUSTADA PARA 4 CASAS):
 * - enquanto digita SEM vírgula: apenas inteiro com milhar (ex: 1234 -> 1.234)
 * - se digitar com vírgula: mantém decimal (ex: 1234,2 -> 1.234,2)
 * - no blur: garante 4 casas decimais (ex: 10 -> 10,0000 | 10,1 -> 10,1000 | 1234 -> 1.234,0000)
 */

function onlyDate(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function formatThousandsBR(intDigits) {
  const s = (intDigits || "").replace(/\D/g, "");
  if (!s) return "";
  return s.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function maskMedidaBRTyping(value) {
  const raw = (value ?? "").toString();

  // mantém apenas dígitos, vírgula e ponto
  let cleaned = raw.replace(/[^\d,\.]/g, "");

  // só permite 1 vírgula
  const firstComma = cleaned.indexOf(",");
  if (firstComma !== -1) {
    cleaned = cleaned.slice(0, firstComma + 1) + cleaned.slice(firstComma + 1).replace(/,/g, "");
  }

  // separa
  let [intPart, decPart = ""] = cleaned.split(",");

  // remove pontos do inteiro e pega apenas dígitos
  intPart = (intPart || "").replace(/\./g, "").replace(/\D/g, "");

  // formata inteiro com milhar
  const intFmt = intPart ? formatThousandsBR(intPart) : "";

  // se não há vírgula, NÃO força decimais enquanto digita
  if (firstComma === -1) return intFmt;

  // se há vírgula, deixa o usuário digitar 0-4 dígitos (sem pad forçado aqui)
  decPart = (decPart || "").replace(/\D/g, "").slice(0, 4);

  // caso usuário digite ",": mantém vírgula visível
  return `${intFmt || "0"},${decPart}`;
}

function maskMedidaBRBlur(value) {
  const raw = (value ?? "").toString().trim();
  if (!raw) return "";

  // reaproveita a typing e então garante 4 casas
  const typing = maskMedidaBRTyping(raw);

  // se não tem vírgula, vira ",0000"
  if (!typing.includes(",")) {
    const intOnly = (typing || "").replace(/\D/g, "");
    const intFmt = intOnly ? formatThousandsBR(intOnly) : "0";
    return `${intFmt},0000`;
  }

  // tem vírgula -> garante 4 casas
  const [i, d = ""] = typing.split(",");
  const dec = (d || "").replace(/\D/g, "").slice(0, 4).padEnd(4, "0");
  const intDigits = (i || "").replace(/\D/g, "");
  const intFmt = intDigits ? formatThousandsBR(intDigits) : "0";
  return `${intFmt},${dec}`;
}

// para edição/visualização (sempre normaliza para 4 casas)
function normalizeMedidaSaved(raw) {
  if (raw === null || raw === undefined) return "";
  const s = raw.toString().trim();
  if (!s) return "";
  return maskMedidaBRBlur(s);
}

export default function IPTU() {
  // ✅ GRID MINIMIZÁVEL (persistência)
  const [gridMinimizado, setGridMinimizado] = useState(() => {
    const saved = localStorage.getItem("iptuGridMinimizado");
    return saved ? saved === "1" : false;
  });

  useEffect(() => {
    localStorage.setItem("iptuGridMinimizado", gridMinimizado ? "1" : "0");
  }, [gridMinimizado]);

  // ✅ STATUS (select - itens cadastrados na tela de STATUS)
  const [statusLista, setStatusLista] = useState([]);

  // Formulário
  const [form, setForm] = useState({
    codigo: "",
    apelido: "",

    inscricao: "",
    setor: "",
    lote: "",
    quadra: "",

    nomeProprietario: "",
    nomeProprietarioGrupo: "",

    // ✅ NOVO: STATUS (select)
    statusId: "",

    // ✅ NOVO: Data de início pagamento IPTU
    dataInicioPagamentoIptu: "",

    area: "",
    frente: "",

    cep: "",
    endereco: "",
    numero: "",
    complemento: "",

    bairro: "",
    cidade: "",
    estado: "", // UF

    possuiOutrasMatriculas: false,
    outrasMatriculas: "",

    observacoes: "",
    id: null, // importante para GED após cadastrar
  });

  // CAMPOS SÃO OBRIGATÓRIOS:
  const required = {
    apelido: true,
    area: true,
    frente: true,
    bairro: true,
    cidade: true,
    estado: true,
  };

  const { validate, fieldProps, clearError, showWarning } = useRequiredFields({
    form,
    required,
    message: "⚠️ Preenchimento obrigatório!\n\nPreencha: Apelido, Área do Terreno, Metros de Frente, Bairro, Cidade e UF.",
  });

  const [apelidos, setApelidos] = useState([]);
  const [imoveisBuscaLista, setImoveisBuscaLista] = useState([]);
  const [modalSelecionarImovel, setModalSelecionarImovel] = useState(false);
  const [filtroImovel, setFiltroImovel] = useState("");
  const [paginaImovel, setPaginaImovel] = useState(0);
  const [linhasPorPaginaImovel, setLinhasPorPaginaImovel] = useState(10);
  const [editandoId, setEditandoId] = useState(null);

  // MODO VISUALIZAÇÃO
  const [visualizandoId, setVisualizandoId] = useState(null);

  // controla quando campos vazios (já salvos) ficam vermelhos
  const [mostrarPendencias, setMostrarPendencias] = useState(false);

  const camposSinalizaveis = [
    "apelido",
    "inscricao",
    "setor",
    "lote",
    "quadra",
    "nomeProprietario",
    "nomeProprietarioGrupo",
    "statusId",
    "dataInicioPagamentoIptu",
    "area",
    "frente",
    "cep",
    "endereco",
    "numero",
    "complemento",
    "bairro",
    "cidade",
    "estado",
    "observacoes",
  ];

  const estaVazio = (v) => !String(v ?? "").trim();

  const classeCampoPendente = (campo) => {
    if (!mostrarPendencias) return "";
    if (!camposSinalizaveis.includes(campo)) return "";
    return estaVazio(form[campo]) ? "campo-pendente" : "";
  };

  const erroCampoMUI = (campo) => {
    if (!mostrarPendencias) return false;
    if (!camposSinalizaveis.includes(campo)) return false;
    return estaVazio(form[campo]);
  };

  const muiCampoSx = {
    "& .MuiOutlinedInput-root": {
      minHeight: 56,
      display: "flex",
      alignItems: "center",

      "& fieldset": {
        borderColor: "var(--ig-b-555555)",
      },

      "&:hover fieldset": {
        borderColor: "var(--ig-b-777777)",
      },

      "&.Mui-focused fieldset": {
        borderColor: "#2ea8ff",
        borderWidth: 2,
      },

      "&.Mui-error fieldset": {
        borderColor: "#ef4444 !important",
        borderWidth: 2,
      },

      "&.Mui-error:hover fieldset": {
        borderColor: "#ef4444 !important",
      },

      "&.Mui-error.Mui-focused fieldset": {
        borderColor: "#ef4444 !important",
        borderWidth: 2,
      },

      "&.Mui-disabled fieldset": {
        borderColor: "var(--ig-b-555555)",
      },
    },

    "& .MuiOutlinedInput-input": {
      padding: "16.5px 14px",
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: "var(--ig-t-ffffff)",
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiInputLabel-root": {
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: "#2ea8ff",
    },

    "& .MuiInputLabel-root.Mui-error": {
      color: "#ef4444 !important",
    },

    "& .MuiInputLabel-root.Mui-disabled": {
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiSvgIcon-root": {
      color: "var(--ig-t-ffffff)",
    },

    '& input[type="date"]::-webkit-calendar-picker-indicator': {
      filter: "var(--ig-x-11o99ka)",
      opacity: 1,
      cursor: "pointer",
    },
  };

  const menuPropsDark = {
    MenuProps: {
      PaperProps: {
        className: "iptu-select-menu-paper",
      },
      MenuListProps: {
        className: "iptu-select-menu-list",
      },
    },
  };

  const campoPadraoClasse =
    "campo-iptu-padrao w-full rounded-md text-[color:var(--ig-t-ffffff)] px-4 py-3 outline-none transition-colors duration-200 placeholder:text-[color:var(--ig-t-9ca3af)]";

  const montarClasseCampo = (campo) => `${campoPadraoClasse} ${classeCampoPendente(campo)}`.trim();

  // PROPRIETÁRIOS (para selects)
  const [proprietariosLista, setProprietariosLista] = useState([]);
  const [proprietarios, setProprietarios] = useState([]);

  // LISTAGEM IPTU
  const [registros, setRegistros] = useState([]);
  const [filtrados, setFiltrados] = useState([]);
  const [busca, setBusca] = useState("");
  const [carregando, setCarregando] = useState(true);

  // GED
  const [arquivos, setArquivos] = useState([]);
  const [modalGED, setModalGED] = useState(false);
  const [idSelecionadoGED, setIdSelecionadoGED] = useState(null);
  const [gedPendentes, setGedPendentes] = useState([]);

  // Matrículas associadas (UI)
  const [listaMatriculas, setListaMatriculas] = useState([]);

  // PAGINAÇÃO
  // PAGINAÇÃO
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [registrosPorPagina, setRegistrosPorPagina] = useState(5);

  const indexInicial = (paginaAtual - 1) * registrosPorPagina;
  const indexFinal = indexInicial + registrosPorPagina;
  const registrosPaginados = filtrados.slice(indexInicial, indexFinal);

  // CEP opcional; se CEP estiver completo (8 dígitos), trava Bairro/Cidade/UF
  const cepDigits = (form.cep || "").replace(/\D/g, "");
  const cepCompleto = cepDigits.length === 8;
  const lockLocalizacao = cepCompleto;

  const imoveisFiltradosBusca = imoveisBuscaLista.filter((imovel) => {
    const termo = filtroImovel.trim().toLowerCase();

    if (!termo) return true;

    const texto = [
      imovel.codigo,
      imovel.codigoMatricula,
      imovel.apelido,
      imovel.endereco,
      imovel.bairro,
      imovel.cidade,
      imovel.estado,
      imovel.uf,
      imovel.status,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return texto.includes(termo);
  });

  const imoveisPaginadosBusca = imoveisFiltradosBusca.slice(paginaImovel * linhasPorPaginaImovel, paginaImovel * linhasPorPaginaImovel + linhasPorPaginaImovel);

  const selecionarImovelApelido = (imovel) => {
    setForm((prev) => ({
      ...prev,
      apelido: imovel.apelido || "",
    }));

    clearError("apelido");
    setModalSelecionarImovel(false);
    setFiltroImovel("");
    setPaginaImovel(0);
  };

  // GED
  const baixarArquivoGED = async (iptuId, arquivoId, nome) => {
    try {
      const resp = await fetch(`/api/iptu/${iptuId}/ged/${arquivoId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!resp.ok) throw new Error("Falha no download");

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = nome || "arquivo";
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Erro ao baixar arquivo.");
    }
  };

  const visualizarArquivoGED = async (iptuId, arquivoId, nomeArquivo = "") => {
    if (!iptuId || !arquivoId) {
      alert("Arquivo inválido para visualização.");
      return;
    }

    const nome = (nomeArquivo || arquivoId || "").toLowerCase();

    const extensoesVisualizaveis = [".pdf", ".png", ".jpg", ".jpeg", ".gif", ".webp", ".bmp", ".svg"];

    const podeVisualizar = extensoesVisualizaveis.some((ext) => nome.endsWith(ext));

    if (!podeVisualizar) {
      const confirmar = window.confirm(
        `Visualização indisponível.\n\nO arquivo "${nomeArquivo}" não possui visualização integrada no ImobGest.\n\nPara visualizar este tipo de arquivo, é necessário baixar e abrir em um aplicativo compatível.\n\nDeseja baixar agora?`,
      );

      if (confirmar) {
        baixarArquivoGED(iptuId, arquivoId, nomeArquivo);
      }

      return;
    }

    try {
      const resp = await fetch(`/api/iptu/${iptuId}/ged/${encodeURIComponent(arquivoId)}/visualizar`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!resp.ok) {
        throw new Error("Erro ao visualizar arquivo GED.");
      }

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);

      window.open(url, "_blank", "noopener,noreferrer");

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 60000);
    } catch (err) {
      console.error(err);
      alert("Erro ao visualizar arquivo GED.");
    }
  };

  const excluirArquivoGED = async (iptuId, arquivoId) => {
    if (!window.confirm("Deseja excluir este arquivo?")) return;

    try {
      const resp = await fetch(`/api/iptu/${iptuId}/ged/${encodeURIComponent(arquivoId)}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!resp.ok) throw new Error("Erro ao excluir GED");

      setArquivos((old) => old.filter((a) => (a.id || a.nome) !== arquivoId));
    } catch (err) {
      console.error(err);
      alert("Erro ao excluir arquivo GED.");
    }
  };

  const enviarGED = async (id, files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    try {
      const resp = await fetch(`/api/iptu/${id}/ged`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: formData,
      });

      return resp.ok;
    } catch (err) {
      console.error("Erro ao enviar GED:", err);
      return false;
    }
  };

  const abrirGED = async (id) => {
    if (!id) return; // ✅ evita /undefined/ged

    setIdSelecionadoGED(id);
    setModalGED(true);

    try {
      const resp = await fetch(`/api/iptu/${id}/ged`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const dados = await resp.json();
      setArquivos(Array.isArray(dados) ? dados : []);
    } catch (err) {
      console.error("Erro ao carregar arquivos GED:", err);
      setArquivos([]);
    }
  };

  useEffect(() => {
    const carregarArquivosDoForm = async () => {
      if (!form.id) return;
      try {
        const resp = await fetch(`/api/iptu/${form.id}/ged`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const dados = await resp.json();
        setArquivos(Array.isArray(dados) ? dados : []);
      } catch {
        setArquivos([]);
      }
    };

    carregarArquivosDoForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.id]);

  // ✅ Carregar Status (para select)
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    fetch("/api/status", { headers })
      .then((res) => res.json())
      .then((data) => {
        const arr = Array.isArray(data) ? data : [];
        setStatusLista(arr);
      })
      .catch((err) => {
        console.error("Erro ao carregar status:", err);
        setStatusLista([]);
      });
  }, []);

  // Carregar apelidos
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    fetch("/api/imoveis", { headers })
      .then((res) => res.json())
      .then((data) => {
        const lista = Array.isArray(data) ? data : [];

        setImoveisBuscaLista(lista);

        const apenasApelidos = lista.map((item) => item.apelido).filter((x) => x && x !== "");

        setApelidos(apenasApelidos);
      })
      .catch((err) => {
        console.error("Erro ao carregar apelidos:", err);
        setApelidos([]);
      });
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    async function carregarProprietarios() {
      try {
        const resp = await fetch("/api/proprietarios", { headers });
        const data = await resp.json();

        const lista = Array.isArray(data) ? data : data?.proprietarios || [];

        const nomes = Array.from(new Set(lista.map((p) => (p.nome ?? "").toString().trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));

        setProprietariosLista(lista);
        setProprietarios(nomes);
      } catch (e) {
        console.error("Erro ao carregar proprietários:", e);
        setProprietarios([]);
      }
    }

    carregarProprietarios();
  }, []);

  // Carregar Lista IPTU
  useEffect(() => {
    carregarIPTUs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carregarIPTUs = async () => {
    try {
      const token = localStorage.getItem("token");

      const headers = {
        "Content-Type": "application/json",
      };

      if (token) headers["Authorization"] = `Bearer ${token}`;

      const resp = await fetch("/api/iptu", { method: "GET", headers });

      if (!resp.ok) {
        console.error("Erro ao carregar IPTUs:", resp.status);
        setRegistros([]);
        setFiltrados([]);
        setCarregando(false);
        return;
      }

      const lista = await resp.json();
      const arr = Array.isArray(lista) ? lista : [];

      setRegistros(arr);
      setFiltrados(arr);
      setCarregando(false);
    } catch (error) {
      console.error("Erro ao listar IPTUs:", error);
      setCarregando(false);
    }
  };

  // FILTRO / EXPORT
  const normalizar = (texto) => {
    return texto
      ?.toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  };

  const handleBusca = (e) => {
    const texto = e.target.value;
    setBusca(texto);

    if (!texto.trim()) {
      setFiltrados(registros);
      setPaginaAtual(1);
      return;
    }

    const termos = texto
      .split(/[,.\s]+/)
      .map((t) => normalizar(t.trim()))
      .filter((t) => t.length > 0);

    const filtradosLocal = registros.filter((item) => {
      const textoItem = normalizar(
        Object.values(item)
          .map((v) => (v === null || v === undefined ? "" : v.toString()))
          .join(" "),
      );

      return termos.every((termo) => textoItem.includes(termo));
    });

    setFiltrados(filtradosLocal);
    setPaginaAtual(1);
  };

  const limparFiltro = () => {
    setBusca("");
    setFiltrados(registros);
    setPaginaAtual(1);
  };

  const copiarTabela = async () => {
    if (!filtrados || filtrados.length === 0) {
      alert("Não há registros para copiar.");
      return;
    }

    const header = [
      "codigo",
      "apelido",
      "Inscrição Municipal",
      "Setor",
      "Lote",
      "Quadra",
      "Nome Proprietário",
      "Nome Proprietário Grupo",
      "Status",
      "Data Início Pagamento IPTU",
      "Área",
      "Frente",
      "CEP",
      "Endereço",
      "Nº",
      "Complemento",
      "Bairro",
      "Cidade",
      "UF",
      "Possui Outras Matrículas",
      "Outras Matrículas",
      "Observações",
    ];

    const linhas = filtrados.map((r) => [
      r.codigo ?? "",
      r.apelido ?? "",
      r.inscricao ?? "",
      r.setor ?? "",
      r.lote ?? "",
      r.quadra ?? "",
      r.nomeProprietario ?? "",
      r.nomeProprietarioGrupo ?? "",
      r.statusId ?? r.status_id ?? r.status ?? "",
      r.dataInicioPagamentoIptu ?? r.data_inicio_pagamento_iptu ?? "",
      r.area ?? "",
      r.frente ?? "",
      r.cep ?? "",
      r.endereco ?? "",
      r.numero ?? "",
      r.complemento ?? "",
      r.bairro ?? "",
      r.cidade ?? "",
      r.estado ?? "",
      r.possuiOutrasMatriculas ? "Sim" : "Não",
      r.outrasMatriculas ?? "",
      r.observacoes ?? "",
    ]);

    const texto = [header, ...linhas].map((l) => l.join("\t")).join("\n");

    const ok = await copiarTexto(texto);
    alert(ok ? "Registros copiados!" : "Não foi possível copiar automaticamente.");
  };

  const exportarCSV = () => {
    // BOM para o Excel (Windows) reconhecer UTF-8 corretamente
    const BOM = "\uFEFF";

    const header = [
      "codigo",
      "apelido",
      "Inscrição Municipal",
      "Setor",
      "Lote",
      "Quadra",
      "Nome Proprietário",
      "Nome Proprietário Grupo",
      "Status",
      "Data Início Pagamento IPTU",
      "Área",
      "Frente",
      "CEP",
      "Endereço",
      "Nº",
      "Complemento",
      "Bairro",
      "Cidade",
      "UF",
      "Possui Outras Matrículas",
      "Outras Matrículas",
      "Observações",
    ];

    // Escapa campo para CSV com ; e aspas
    const csvEscape = (value) => {
      const s = String(value ?? "");
      if (/[;"\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    // Força Excel a tratar como TEXTO: ="..."
    const excelText = (value) => `="${String(value ?? "")}"`;

    const linhas = (filtrados || []).map((r) =>
      [
        r.codigo ?? "",
        r.apelido ?? "",

        // Campos "sensíveis" como texto
        excelText(r.inscricao ?? ""),
        excelText(r.setor ?? ""),
        excelText(r.lote ?? ""),
        excelText(r.quadra ?? ""),

        r.nomeProprietario ?? "",
        r.nomeProprietarioGrupo ?? "",
        excelText(r.statusId ?? r.status_id ?? r.status ?? ""),
        excelText(r.dataInicioPagamentoIptu ?? r.data_inicio_pagamento_iptu ?? ""),

        // ✅ Medidas iguais ao sistema (pt-BR, 4 casas) e como TEXTO no Excel
        excelText(normalizeMedidaSaved(r.area ?? "")),
        excelText(normalizeMedidaSaved(r.frente ?? "")),

        // CEP como texto (para não perder zero)
        excelText(r.cep ?? ""),

        r.endereco ?? "",
        excelText(r.numero ?? ""),
        r.complemento ?? "",
        r.bairro ?? "",
        r.cidade ?? "",
        r.estado ?? "",

        r.possuiOutrasMatriculas ? "Sim" : "Não",

        // pode ter zeros, separadores etc. -> texto
        excelText(r.outrasMatriculas ?? ""),

        r.observacoes ?? "",
      ].map(csvEscape),
    );

    const csv = [header.map(csvEscape), ...linhas].map((l) => l.join(";")).join("\n");

    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "iptu.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // CRUD
  const editarRegistro = (item) => {
    setForm((prev) => ({
      ...prev,
      ...item,

      nomeProprietario: item.nomeProprietario ?? "",
      nomeProprietarioGrupo: item.nomeProprietarioGrupo ?? "",
      endereco: item.endereco ?? "",
      numero: item.numero ?? "",
      complemento: item.complemento ?? "",

      // ✅ STATUS (aceita diferentes nomes vindos do backend, sem quebrar)
      statusId: item.statusId ?? item.status_id ?? "",

      // ✅ NOVO: Data de início pagamento IPTU
      dataInicioPagamentoIptu: item.dataInicioPagamentoIptu ?? item.data_inicio_pagamento_iptu ?? "",

      area: normalizeMedidaSaved(item.area ?? ""),
      frente: normalizeMedidaSaved(item.frente ?? ""),

      possuiOutrasMatriculas: !!item.possuiOutrasMatriculas,
      outrasMatriculas: item.outrasMatriculas ?? "",
      observacoes: item.observacoes ?? "",

      id: item.id ?? prev.id,
    }));

    setEditandoId(item.id);
    setVisualizandoId(null);

    const texto = (item.outrasMatriculas ?? "").toString();
    const arr = texto
      .split(",")
      .map((x) => x.trim().toUpperCase())
      .filter(Boolean);

    setListaMatriculas(
      arr.map((codigo) => ({
        matriculaId: null,
        codigoMatricula: codigo,
        apelido: "",
        label: codigo,
      })),
    );

    setMostrarPendencias(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const visualizarRegistro = (item) => {
    setForm((prev) => ({
      ...prev,
      ...item,

      nomeProprietario: item.nomeProprietario ?? "",
      nomeProprietarioGrupo: item.nomeProprietarioGrupo ?? "",
      endereco: item.endereco ?? "",
      numero: item.numero ?? "",
      complemento: item.complemento ?? "",

      // ✅ STATUS (aceita diferentes nomes vindos do backend, sem quebrar)
      statusId: item.statusId ?? item.status_id ?? item.statusID ?? item.status ?? "",

      // ✅ NOVO: Data de início pagamento IPTU
      dataInicioPagamentoIptu: item.dataInicioPagamentoIptu ?? item.data_inicio_pagamento_iptu ?? "",

      area: normalizeMedidaSaved(item.area ?? ""),
      frente: normalizeMedidaSaved(item.frente ?? ""),

      possuiOutrasMatriculas: !!item.possuiOutrasMatriculas,
      outrasMatriculas: item.outrasMatriculas ?? "",
      observacoes: item.observacoes ?? "",
      id: item.id ?? prev.id,
    }));

    setEditandoId(null);
    setVisualizandoId(item.id);

    const texto = (item.outrasMatriculas ?? "").toString();
    const arr = texto
      .split(",")
      .map((x) => x.trim().toUpperCase())
      .filter(Boolean);

    setListaMatriculas(
      arr.map((codigo) => ({
        matriculaId: null,
        codigoMatricula: codigo,
        apelido: "",
        label: codigo,
      })),
    );

    setMostrarPendencias(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const excluirRegistro = async (item) => {
    const idIptu = item?.id;
    if (!idIptu) {
      alert("Registro inválido para exclusão.");
      return;
    }
    if (!window.confirm("Deseja excluir este registro?")) return;

    try {
      await fetch(`/api/iptu/${idIptu}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      alert("IPTU excluído!");
      await carregarIPTUs();
      limparFormulario();
    } catch {
      alert("Erro ao excluir IPTU.");
    }
  };

  // FORM
  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;

    const camposMaiusculos = [
      "inscricao",
      "setor",
      "lote",
      "quadra",
      "nomeProprietario",
      "nomeProprietarioGrupo",
      "endereco",
      "numero",
      "complemento",
      "bairro",
      "cidade",
      "estado",
      "observacoes",
    ];

    let novoValor = value;

    if (type !== "checkbox" && camposMaiusculos.includes(name)) {
      novoValor = (value || "").toUpperCase();
    }

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : novoValor,
    }));
  };

  const mascaraCEP = (valor) => {
    valor = (valor || "").replace(/\D/g, "");
    return valor.replace(/(\d{5})(\d)/, "$1-$2");
  };

  const buscarCEP = async () => {
    const numero = (form.cep || "").replace(/\D/g, "");
    if (numero.length !== 8) return;

    try {
      const resp = await fetch(`https://viacep.com.br/ws/${numero}/json/`);
      const dados = await resp.json();

      if (dados.erro) {
        alert("CEP não encontrado!");
        return;
      }

      setForm((prev) => ({
        ...prev,
        endereco: dados.logradouro || prev.endereco,
        bairro: dados.bairro,
        cidade: dados.localidade,
        estado: dados.uf,
      }));
    } catch {
      alert("Erro ao consultar CEP.");
    }
  };

  const limparFormulario = () => {
    setForm({
      codigo: "",
      apelido: "",

      inscricao: "",
      setor: "",
      lote: "",
      quadra: "",

      nomeProprietario: "",
      nomeProprietarioGrupo: "",

      // ✅ NOVO: STATUS (select)
      statusId: "",

      // ✅ NOVO: Data de início pagamento IPTU
      dataInicioPagamentoIptu: "",

      area: "",
      frente: "",

      cep: "",
      endereco: "",
      numero: "",
      complemento: "",

      bairro: "",
      cidade: "",
      estado: "",

      possuiOutrasMatriculas: false,
      outrasMatriculas: "",

      observacoes: "",
      id: null,
    });

    setEditandoId(null);
    setVisualizandoId(null);
    setListaMatriculas([]);
    setGedPendentes([]);
    setArquivos([]);
    setModalGED(false);
    setIdSelecionadoGED(null);
    setMostrarPendencias(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (visualizandoId) return;

    setMostrarPendencias(true);

    if (!validate()) return;

    try {
      let payload = { ...form };

      // ✅ garante que vai salvar sempre com 4 casas
      payload.area = maskMedidaBRBlur(form.area);
      payload.frente = maskMedidaBRBlur(form.frente);

      // ✅ envia também como status_id para compatibilidade backend (se ele esperar snake_case)
      payload.status_id = form.statusId ? Number(form.statusId) : null;
      payload.statusId = payload.status_id; // opcional, mas ajuda compatibilidade

      // ✅ envia também como snake_case para compatibilidade backend
      payload.data_inicio_pagamento_iptu = form.dataInicioPagamentoIptu || null;

      const codigos = Array.from(
        new Set((listaMatriculas || []).map((m) => (m?.codigoMatricula || m?.label || "").toString().trim().toUpperCase()).filter(Boolean)),
      );

      if (form.possuiOutrasMatriculas) {
        if (codigos.length === 0) {
          alert("Adicione ao menos uma matrícula.");
          return;
        }
        payload.outrasMatriculas = codigos.join(", ");
      } else {
        payload.outrasMatriculas = "";
      }

      let resp;

      if (editandoId) {
        resp = await fetch(`/api/iptu/${editandoId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(payload),
        });

        const dados = await resp.json();
        const codigo = dados.codigo ?? dados[0]?.codigo ?? "N/D";
        alert(`IPTU atualizado com sucesso! Código: ${codigo}`);

        setForm((prev) => ({ ...prev, id: editandoId }));
        setMostrarPendencias(true);

        if (gedPendentes.length > 0) {
          const okGed = await enviarGED(editandoId, gedPendentes);

          if (okGed) {
            setGedPendentes([]);
            await carregarIPTUs();
            await abrirGED(editandoId);
            alert("Anexos GED pendentes enviados!");
          } else {
            await carregarIPTUs();
            alert("IPTU atualizado, mas falhou ao enviar os anexos GED pendentes.");
          }
        } else {
          await carregarIPTUs();
        }

        setVisualizandoId(editandoId);
        setEditandoId(null);

        setForm((prev) => ({
          ...prev,
          id: editandoId,
        }));

        return;
      }

      resp = await fetch("/api/iptu", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      const dados = await resp.json();

      alert(`IPTU cadastrado! Código: ${dados.codigo}`);

      setForm((prev) => ({ ...prev, id: dados.id }));
      setMostrarPendencias(true);

      if (gedPendentes.length > 0) {
        const okGed = await enviarGED(dados.id, gedPendentes);

        if (okGed) {
          setGedPendentes([]);
          await carregarIPTUs();
          await abrirGED(dados.id);
          alert("Anexos GED pendentes enviados!");
        } else {
          await carregarIPTUs();
          alert("IPTU cadastrado, mas falhou ao enviar os anexos GED pendentes.");
        }
      } else {
        await carregarIPTUs();
      }
    } catch (err) {
      console.error("Erro ao salvar IPTU:", err);
      alert("Erro ao salvar IPTU.");
    }
  };

  // PERMISSÕES
  const token = localStorage.getItem("token");
  let tipoUsuario = "";

  if (token) {
    const decoded = jwtDecode(token);

    tipoUsuario = decoded.tipo
      ?.toString()
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(
    // eslint-disable-next-line
    tipoUsuario,
  );
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeEditar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["gestor", "administrador"].includes(tipoUsuario);

  const emVisualizacao = !!visualizandoId && !editandoId;
  const emEdicao = !!editandoId;
  const formReadOnly = emVisualizacao || !podeCadastrar;
  const podeRenderForm = podeVisualizar || podeCadastrar || podeEditar || podeExcluir;

  const adicionarMatriculaSelecionada = (sel) => {
    if (!sel) return;

    const matriculaId = sel.id ?? null;
    const codigoMatricula = (sel.codigoMatricula ?? "").toString().trim().toUpperCase();
    const apelido = (sel.apelido ?? "").toString().trim();
    const label = apelido ? `${codigoMatricula} - ${apelido}` : codigoMatricula;

    if (!codigoMatricula) return;

    setListaMatriculas((prev) => {
      const jaExiste = prev.some((x) => (x.codigoMatricula || "").toString().trim().toUpperCase() === codigoMatricula);
      if (jaExiste) return prev;

      return [...prev, { matriculaId, codigoMatricula, apelido, label }];
    });
  };

  const temGedNoForm = (form.id && arquivos.length > 0) || gedPendentes.length > 0 || (registros.find((r) => r.id === form.id)?.qtdArquivos || 0) > 0;

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="local-container">
        <h2>IPTU</h2>

        {/* FILTRO PADRÃO MUI */}
        <Box
          className="bloco-linha"
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              height: 52,
              width: "100%",
              border: "1px solid var(--ig-b-555555)",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              backgroundColor: "transparent",
              boxSizing: "border-box",
              px: 2,
            }}
          >
            <input
              type="text"
              placeholder="Buscar IPTU"
              value={busca}
              onChange={handleBusca}
              style={{
                flex: 1,
                height: "100%",
                border: "none",
                outline: "none",
                background: "transparent",
                color: "var(--ig-t-ffffff)",
                fontSize: "1rem",
              }}
            />

            <SearchIcon
              sx={{
                color: "var(--ig-t-9ca3af)",
                fontSize: 20,
                cursor: "pointer",
                flexShrink: 0,
              }}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              type="button"
              onClick={limparFiltro}
              sx={{
                height: 52,
                color: "#2196f3",
                borderColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  borderColor: "#42a5f5",
                  backgroundColor: "rgba(33, 150, 243, 0.08)",
                },
              }}
            >
              Limpar Filtro
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={copiarTabela}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              <FaCopy style={{ marginRight: 6 }} />
              Copiar
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={exportarCSV}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              CSV
            </Button>
          </Box>
        </Box>

        {/* ✅ BOTÃO MINIMIZAR / EXPANDIR (sempre visível) */}
        <div className="grid-toggle-area right">
          {!gridMinimizado ? (
            <button type="button" className="btn-grid-toggle small danger" onClick={() => setGridMinimizado(true)} title="Minimizar o Grid">
              ↘ Minimizar
            </button>
          ) : (
            <button type="button" className="btn-grid-toggle small outline" onClick={() => setGridMinimizado(false)} title="Expandir o Grid">
              ↗ Expandir
            </button>
          )}
        </div>

        {/* LISTAGEM (tabela + paginação somem quando minimizado) */}
        {!gridMinimizado && (
          <div className="bloco-lista">
            {carregando ? (
              <p>Carregando...</p>
            ) : filtrados.length === 0 ? (
              <p>Nenhum IPTU encontrado</p>
            ) : (
              <>
                <div className="tabela-scroll">
                  <table className="tabela-matriculas">
                    <colgroup>
                      <col style={{ width: "9%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "12%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "23%" }} />
                    </colgroup>

                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Apelido</th>
                        <th>Cidade</th>
                        <th>Inscrição</th>
                        <th>Lote</th>
                        <th>Área</th>
                        <th>Ações</th>
                      </tr>
                    </thead>

                    <tbody>
                      {registrosPaginados.map((item) => (
                        <tr key={item.id || item.apelido || item.inscricao}>
                          <td>{item.codigo}</td>
                          <td>{item.apelido}</td>
                          <td>{item.cidade}</td>
                          <td>{item.inscricao}</td>
                          <td>{item.lote}</td>

                          {/* ✅ exibe sempre com 4 casas */}
                          <td>{normalizeMedidaSaved(item.area)}</td>

                          <td className="col-acoes">
                            <button
                              className={`btn-ged-lista ${item.qtdArquivos > 0 ? "btn-ged-ok" : ""}`}
                              type="button"
                              onClick={() => item.id && abrirGED(item.id)}
                            >
                              GED
                              {item.qtdArquivos > 0 && <span className="ged-badge">{item.qtdArquivos}</span>}
                            </button>

                            {podeVisualizar && (
                              <button className="btn-editar" type="button" onClick={() => visualizarRegistro(item)}>
                                <FaEye /> Visualizar
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <TablePagination
                  component="div"
                  count={filtrados.length}
                  page={paginaAtual - 1}
                  onPageChange={(_, novaPagina) => setPaginaAtual(novaPagina + 1)}
                  rowsPerPage={registrosPorPagina}
                  onRowsPerPageChange={(e) => {
                    setRegistrosPorPagina(parseInt(e.target.value, 10));
                    setPaginaAtual(1);
                  }}
                  rowsPerPageOptions={[5, 10, 25]}
                  labelRowsPerPage="IPTU por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          backgroundColor: "var(--ig-f-111827) !important",
                          color: "var(--ig-t-ffffff)",

                          "& .MuiList-root": {
                            backgroundColor: "var(--ig-f-111827)",
                            padding: 0,
                          },

                          "& .MuiMenuItem-root": {
                            backgroundColor: "var(--ig-f-111827)",
                            color: "var(--ig-t-ffffff)",
                          },

                          "& .MuiMenuItem-root:hover": {
                            backgroundColor: "var(--ig-f-1f2937)",
                          },

                          "& .MuiMenuItem-root.Mui-selected": {
                            backgroundColor: "var(--ig-f-1f2937) !important",
                          },

                          "& .MuiMenuItem-root.Mui-selected:hover": {
                            backgroundColor: "#374151 !important",
                          },
                        },
                      },
                    },
                  }}
                  sx={{
                    fontSize: "0.8rem",
                    minHeight: 42,
                    color: "var(--ig-t-ffffff)",

                    "& .MuiTablePagination-toolbar": {
                      minHeight: 42,
                      paddingLeft: 0,
                      paddingRight: 0,
                    },

                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                      fontSize: "0.8rem",
                    },

                    "& .MuiTablePagination-selectIcon": {
                      color: "var(--ig-t-ffffff)",
                    },

                    "& .MuiTablePagination-actions button": {
                      color: "var(--ig-t-ffffff)",
                      width: "auto",
                    },

                    "& .MuiTablePagination-actions": {
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                />
              </>
            )}
          </div>
        )}

        {/* MODAL GED */}
        {/* MODAL SELECIONAR IMÓVEL */}
        <Dialog
          open={modalSelecionarImovel}
          onClose={() => setModalSelecionarImovel(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: "var(--ig-f-2c2c2c)",
              color: "var(--ig-t-ffffff)",
              backgroundImage: "none",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              color: "var(--ig-t-ffffff)",
              fontWeight: 700,
            }}
          >
            Selecionar Imóvel
            <IconButton onClick={() => setModalSelecionarImovel(false)}>
              <CloseIcon sx={{ color: "var(--ig-t-aaaaaa)" }} />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                value={filtroImovel}
                onChange={(e) => {
                  setFiltroImovel(e.target.value);
                  setPaginaImovel(0);
                }}
                placeholder="Código, apelido, endereço, bairro ou cidade..."
                fullWidth
                size="small"
                sx={muiCampoSx}
              />

              <Button variant="contained" sx={{ minWidth: 110 }}>
                Pesquisar
              </Button>
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Apelido</TableCell>
                  <TableCell>Endereço</TableCell>
                  <TableCell>Bairro</TableCell>
                  <TableCell>Cidade/UF</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {imoveisPaginadosBusca.map((imovel) => (
                  <TableRow key={imovel.id || imovel.codigo || imovel.apelido} hover onClick={() => selecionarImovelApelido(imovel)} sx={{ cursor: "pointer" }}>
                    <TableCell>{imovel.codigo || imovel.codigoMatricula || ""}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{imovel.apelido || ""}</TableCell>
                    <TableCell>{imovel.endereco || ""}</TableCell>
                    <TableCell>{imovel.bairro || ""}</TableCell>
                    <TableCell>{[imovel.cidade, imovel.estado || imovel.uf].filter(Boolean).join("/")}</TableCell>
                    <TableCell>{imovel.status || ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={imoveisFiltradosBusca.length}
              page={paginaImovel}
              onPageChange={(_, novaPagina) => setPaginaImovel(novaPagina)}
              rowsPerPage={linhasPorPaginaImovel}
              onRowsPerPageChange={(e) => {
                setLinhasPorPaginaImovel(parseInt(e.target.value, 10));
                setPaginaImovel(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              sx={{
                color: "var(--ig-t-ffffff)",
                "& .MuiTablePagination-selectIcon": {
                  color: "var(--ig-t-ffffff)",
                },
                "& .MuiTablePagination-actions button": {
                  color: "var(--ig-t-ffffff)",
                },
              }}
            />
          </DialogContent>
        </Dialog>

        {/* MODAL GED */}
        {modalGED && (
          <div className="modal-ged-overlay">
            <div className="modal-ged">
              <h3>Arquivos GED - IPTU #{idSelecionadoGED}</h3>

              {arquivos.length === 0 ? (
                <p>Nenhum arquivo cadastrado para este IPTU.</p>
              ) : (
                <ul style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {arquivos.map((arq) => {
                    const arquivoId = arq.id || arq.nome;

                    return (
                      <li key={arquivoId} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span style={{ flex: 1 }}>{arq.nome}</span>

                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <button type="button" className="btn-visualizar-ged" onClick={() => visualizarArquivoGED(idSelecionadoGED, arquivoId, arq.nome)}>
                            <FaEye /> Visualizar
                          </button>

                          <button type="button" className="btn-baixar-ged" onClick={() => baixarArquivoGED(idSelecionadoGED, arquivoId, arq.nome)}>
                            Baixar
                          </button>

                          <button type="button" className="btn-excluir-ged" onClick={() => excluirArquivoGED(idSelecionadoGED, arquivoId)}>
                            <FaTrash /> Excluir
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              <button type="button" className="btn-fechar-ged" onClick={() => setModalGED(false)}>
                Fechar
              </button>
            </div>
          </div>
        )}
        {/* FORMULÁRIO */}
        {podeRenderForm && podeVisualizar ? (
          <div className="bloco-formulario">
            <form className="form-grid" onSubmit={handleSubmit}>
              {/* Linha 1: Apelido / Nome Proprietário / Grupo Proprietário */}
              <div className="form-row iptu-row-1">
                <TextField
                  label="Apelido*"
                  value={form.apelido || ""}
                  onClick={() => {
                    if (formReadOnly) return;
                    setModalSelecionarImovel(true);
                  }}
                  disabled={formReadOnly}
                  error={erroCampoMUI("apelido")}
                  fullWidth
                  size="small"
                  sx={{
                    ...muiCampoSx,
                    cursor: "pointer",
                    "& .MuiInputBase-input": {
                      cursor: "pointer",
                      color: "var(--ig-t-ffffff)",
                    },
                  }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: <SearchIcon sx={{ color: "var(--ig-t-ffffff)" }} />,
                  }}
                  placeholder="Clique para selecionar o imóvel"
                />

                <TextField
                  select
                  label="Nome Proprietário"
                  value={form.nomeProprietario}
                  onChange={(e) => {
                    const nomeSelecionado = e.target.value;

                    const proprietarioEncontrado = proprietariosLista.find((p) => (p.nome || "").trim() === nomeSelecionado);

                    setForm((prev) => ({
                      ...prev,
                      nomeProprietario: nomeSelecionado,
                      nomeProprietarioGrupo: proprietarioEncontrado?.grupo || "",
                    }));
                  }}
                  disabled={formReadOnly}
                  error={erroCampoMUI("nomeProprietario")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  slotProps={{
                    select: menuPropsDark,
                  }}
                >
                  <MenuItem value="">Selecione</MenuItem>

                  {form.nomeProprietario && !proprietarios.includes(form.nomeProprietario) && (
                    <MenuItem value={form.nomeProprietario}>{form.nomeProprietario} (atual)</MenuItem>
                  )}

                  {proprietarios.map((nome) => (
                    <MenuItem key={nome} value={nome}>
                      {nome}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Grupo Proprietário"
                  name="nomeProprietarioGrupo"
                  value={form.nomeProprietarioGrupo}
                  disabled
                  error={erroCampoMUI("nomeProprietarioGrupo")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />
              </div>

              <div className="form-row">
                <TextField
                  label="Inscrição Municipal"
                  name="inscricao"
                  value={form.inscricao}
                  onChange={handleChange}
                  disabled={formReadOnly}
                  error={erroCampoMUI("inscricao")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />
                <TextField
                  label="Setor"
                  name="setor"
                  value={form.setor ?? ""}
                  onChange={handleChange}
                  disabled={formReadOnly}
                  error={erroCampoMUI("setor")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />
                <TextField
                  label="Lote"
                  name="lote"
                  value={form.lote}
                  onChange={handleChange}
                  disabled={formReadOnly}
                  error={erroCampoMUI("lote")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />
                <TextField
                  label="Quadra"
                  name="quadra"
                  value={form.quadra}
                  onChange={handleChange}
                  disabled={formReadOnly}
                  error={erroCampoMUI("quadra")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />
              </div>

              <div className="form-row">
                <TextField
                  select
                  label="Status"
                  name="statusId"
                  value={form.statusId ?? ""}
                  onChange={handleChange}
                  disabled={formReadOnly}
                  error={erroCampoMUI("statusId")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  slotProps={{
                    select: menuPropsDark,
                  }}
                >
                  <MenuItem value="">Selecione</MenuItem>

                  {(statusLista || []).map((s) => {
                    const id = s.id ?? s.status_id;
                    return (
                      <MenuItem key={id ?? s.nome} value={id ? String(id) : ""}>
                        {s.nome ?? ""}
                      </MenuItem>
                    );
                  })}
                </TextField>

                <TextField
                  label="Data de Início Pagamento IPTU"
                  type="date"
                  name="dataInicioPagamentoIptu"
                  value={form.dataInicioPagamentoIptu ?? ""}
                  onChange={handleChange}
                  disabled={formReadOnly}
                  error={erroCampoMUI("dataInicioPagamentoIptu")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              </div>

              <div className="form-row">
                <TextField
                  label="Área Terreno (M²)*"
                  value={form.area}
                  inputMode="decimal"
                  disabled={formReadOnly}
                  error={erroCampoMUI("area")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  onChange={(e) => {
                    if (formReadOnly) return;
                    const masked = maskMedidaBRTyping(e.target.value);
                    setForm((f) => ({ ...f, area: masked }));
                    if (masked?.trim()) clearError("area");
                  }}
                  onBlur={() => {
                    if (formReadOnly) return;
                    setForm((f) => ({ ...f, area: maskMedidaBRBlur(f.area) }));
                  }}
                />

                <TextField
                  label="Metros de Frente (M²)*"
                  value={form.frente}
                  inputMode="decimal"
                  disabled={formReadOnly}
                  error={erroCampoMUI("frente")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  onChange={(e) => {
                    if (formReadOnly) return;
                    const masked = maskMedidaBRTyping(e.target.value);
                    setForm((f) => ({ ...f, frente: masked }));
                    if (masked?.trim()) clearError("frente");
                  }}
                  onBlur={() => {
                    if (formReadOnly) return;
                    setForm((f) => ({ ...f, frente: maskMedidaBRBlur(f.frente) }));
                  }}
                />
              </div>

              <div className="form-row iptu-row-4">
                <TextField
                  label="CEP"
                  name="cep"
                  value={form.cep}
                  disabled={formReadOnly}
                  error={erroCampoMUI("cep")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  inputProps={{ maxLength: 9 }}
                  onChange={(e) => {
                    if (formReadOnly) return;
                    setForm((f) => ({ ...f, cep: mascaraCEP(e.target.value) }));
                  }}
                  onBlur={() => {
                    if (formReadOnly) return;
                    buscarCEP();
                  }}
                />

                <TextField
                  label="Endereço"
                  name="endereco"
                  value={form.endereco}
                  onChange={handleChange}
                  disabled={formReadOnly}
                  error={erroCampoMUI("endereco")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />
                <TextField
                  label="Nº"
                  name="numero"
                  value={form.numero}
                  onChange={handleChange}
                  disabled={formReadOnly}
                  error={erroCampoMUI("numero")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />
              </div>

              <div className="form-row iptu-row-5">
                <TextField
                  label="Complemento"
                  name="complemento"
                  value={form.complemento}
                  onChange={handleChange}
                  disabled={formReadOnly}
                  error={erroCampoMUI("complemento")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />

                <TextField
                  label="Bairro*"
                  name="bairro"
                  value={form.bairro}
                  onChange={(e) => {
                    handleChange(e);
                    if (e.target.value?.trim()) clearError("bairro");
                  }}
                  disabled={formReadOnly || lockLocalizacao}
                  error={erroCampoMUI("bairro")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />

                <TextField
                  label="Cidade*"
                  name="cidade"
                  value={form.cidade}
                  onChange={(e) => {
                    handleChange(e);
                    if (e.target.value?.trim()) clearError("cidade");
                  }}
                  disabled={formReadOnly || lockLocalizacao}
                  error={erroCampoMUI("cidade")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />

                <TextField
                  label="UF*"
                  name="estado"
                  value={form.estado}
                  onChange={(e) => {
                    handleChange(e);
                    if (e.target.value?.trim()) clearError("estado");
                  }}
                  disabled={formReadOnly || lockLocalizacao}
                  error={erroCampoMUI("estado")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />
              </div>

              {showWarning && <div className="aviso-obrigatorio">Preencha os campos obrigatórios destacados em vermelho.</div>}

              {/* 6) Outras matrículas */}
              <div className="form-row" style={{ alignItems: "flex-end" }}>
                <label style={{ width: "420px", marginTop: "4px" }}>
                  Existem mais matrículas atreladas a este IPTU
                  <select
                    name="possuiOutrasMatriculas"
                    value={form.possuiOutrasMatriculas ? "SIM" : "NAO"}
                    disabled={formReadOnly}
                    onChange={(e) => {
                      if (formReadOnly) return;
                      const ehSim = e.target.value === "SIM";
                      setForm((prev) => ({
                        ...prev,
                        possuiOutrasMatriculas: ehSim,
                        outrasMatriculas: ehSim ? prev.outrasMatriculas : "",
                      }));

                      if (!ehSim) setListaMatriculas([]);
                    }}
                    style={{ width: "240px", marginTop: "15px" }}
                    className={campoPadraoClasse}
                  >
                    <option value="NAO">Não</option>
                    <option value="SIM">Sim</option>
                  </select>
                </label>
              </div>

              {/* 7) Se SIM, BuscarMatricula */}
              {form.possuiOutrasMatriculas && (
                <>
                  <div className="form-row">
                    <label style={{ width: "100%", marginTop: "10px" }}>Adicionar Matrícula</label>
                  </div>

                  {listaMatriculas.length > 0 && (
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      {listaMatriculas.map((m, idx) => (
                        <div
                          key={`${m.codigoMatricula || m.label}-${idx}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "16px",
                            width: "100%",
                          }}
                        >
                          <input value={m.label || m.codigoMatricula || ""} readOnly style={{ flex: 1 }} className={campoPadraoClasse} />
                          {!formReadOnly && (
                            <button
                              type="button"
                              onClick={() => setListaMatriculas((prev) => prev.filter((_, i) => i !== idx))}
                              style={{
                                background: "#c62828",
                                color: "#fff",
                                border: "none",
                                padding: "10px 12px",
                                borderRadius: "10px",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                                width: "110px",
                                height: "48px",
                              }}
                            >
                              Remover
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {!formReadOnly && (
                    <div style={{ width: "100%", marginTop: "6px" }}>
                      <BuscarMatricula onSelecionar={adicionarMatriculaSelecionada} />
                    </div>
                  )}
                </>
              )}

              {/* 8) Observações */}
              <div className="form-row">
                <label style={{ width: "100%" }}>
                  Observações
                  <textarea
                    name="observacoes"
                    value={form.observacoes}
                    onChange={handleChange}
                    rows={3}
                    style={{ width: "100%", resize: "vertical" }}
                    readOnly={formReadOnly}
                    className={montarClasseCampo("observacoes")}
                  />
                </label>
              </div>

              {/* BOTÕES */}
              <div className="iptu-botoes">
                {emVisualizacao ? (
                  <>
                    {podeEditar && (
                      <button
                        type="button"
                        className="btn-cadastrar iptu-btn-acao"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setEditandoId(visualizandoId);
                          setVisualizandoId(null);
                        }}
                      >
                        <FaEdit />
                        Editar
                      </button>
                    )}

                    {podeExcluir && (
                      <button type="button" className="btn-excluir iptu-btn-acao" onClick={() => excluirRegistro({ id: form.id })}>
                        <FaTrash /> Excluir
                      </button>
                    )}

                    <button type="button" className="btn-cancelar iptu-btn-acao" onClick={limparFormulario}>
                      <FaTimes /> Cancelar
                    </button>
                  </>
                ) : emEdicao ? (
                  <>
                    {podeEditar && (
                      <button type="submit" className="btn-cadastrar iptu-btn-acao">
                        <FaSave /> Atualizar
                      </button>
                    )}

                    <button type="button" className="btn-cancelar iptu-btn-acao" onClick={limparFormulario}>
                      <FaTimes /> Cancelar
                    </button>
                  </>
                ) : (
                  <>
                    {podeCadastrar && (
                      <button type="submit" className="btn-cadastrar iptu-btn-acao">
                        <FaSave /> Cadastrar
                      </button>
                    )}

                    <button type="button" className="btn-cancelar iptu-btn-acao" onClick={limparFormulario}>
                      <FaTimes /> Cancelar
                    </button>
                  </>
                )}

                {/* GED */}
                <button
                  type="button"
                  className={`btn-ged btn-ged-form iptu-btn-acao ${temGedNoForm ? "btn-ged-ok" : ""}`}
                  onClick={() => {
                    document.getElementById("uploadGED")?.click();
                  }}
                  disabled={!form.id && !podeCadastrar}
                >
                  <FaPaperclip />
                  GED
                  {form.id &&
                    (() => {
                      const reg = registros.find((r) => r.id === form.id);
                      return reg?.qtdArquivos > 0 ? <span className="ged-badge">{reg.qtdArquivos}</span> : null;
                    })()}
                  {gedPendentes.length > 0 && !form.id && <span className="ged-badge">{gedPendentes.length}</span>}
                </button>
              </div>

              {/* LISTA GED (PENDENTES) */}
              {!formReadOnly && gedPendentes.length > 0 && (
                <div style={{ width: "100%", marginTop: "12px" }}>
                  <label style={{ display: "block", marginBottom: "8px" }}>Arquivos pendentes para anexar</label>

                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {gedPendentes.map((f, idx) => (
                      <div key={`${f.name}-${idx}`} style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                        <input value={f.name} readOnly style={{ flex: 1 }} className={campoPadraoClasse} />

                        <button
                          type="button"
                          onClick={() => setGedPendentes((prev) => prev.filter((_, i) => i !== idx))}
                          style={{
                            background: "#c62828",
                            color: "#fff",
                            border: "none",
                            padding: "10px 12px",
                            borderRadius: "10px",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            width: "110px",
                            height: "48px",
                          }}
                        >
                          Remover
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* LISTA GED (ANEXADOS) */}
              {form.id && arquivos.length > 0 && (
                <div style={{ width: "100%", marginTop: "12px" }}>
                  <label style={{ display: "block", marginBottom: "8px" }}>Arquivos anexados neste IPTU</label>

                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {arquivos.map((arq) => {
                      const arquivoId = arq.id || arq.nome;

                      return (
                        <div key={arquivoId} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <input value={arq.nome} readOnly style={{ flex: 1 }} className={campoPadraoClasse} />

                          <button type="button" className="btn-arquivo-ged" onClick={() => baixarArquivoGED(form.id, arquivoId, arq.nome)}>
                            Baixar
                          </button>

                          <button
                            type="button"
                            onClick={() => excluirArquivoGED(form.id, arquivoId)}
                            style={{
                              background: "#c62828",
                              color: "#fff",
                              border: "none",
                              padding: "10px 12px",
                              borderRadius: "10px",
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              width: "110px",
                              height: "48px",
                            }}
                          >
                            Excluir
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Input escondido para upload GED */}
              <input
                type="file"
                id="uploadGED"
                multiple
                style={{ display: "none" }}
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  if (!files.length) return;

                  e.target.value = "";

                  if (formReadOnly && !form.id) return;

                  if (!form.id) {
                    setGedPendentes((prev) => [...prev, ...files]);
                    alert(`Arquivos preparados (${files.length}). Clique em Cadastrar para anexar.`);
                    return;
                  }

                  const ok = await enviarGED(form.id, files);

                  if (ok) {
                    alert("Arquivos enviados!");
                    await abrirGED(form.id);
                    await carregarIPTUs();
                  } else {
                    alert("Falha ao enviar arquivos.");
                  }
                }}
              />
            </form>
          </div>
        ) : (
          <div className="aviso-permissao-container">
            <p className="aviso-permissao">🔒 Você não possui permissão para visualizar.</p>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
}
